import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { StoreProvider } from "@/context/StoreContext";
import { Layout } from "@/components/layout/Layout";
import { ProtectedRoute } from "@/components/common/ProtectedRoute";
import { Toaster } from "sonner";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import VendorStore from "@/pages/VendorStore";
import Vendors from "@/pages/Vendors";
import B2B from "@/pages/B2B";
import RequestQuote from "@/pages/RequestQuote";
import Cart from "@/pages/Cart";
import Wishlist from "@/pages/Wishlist";
import Blog from "@/pages/Blog";
import Referral from "@/pages/Referral";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Account from "@/pages/account/Account";
import VendorRegister from "@/pages/vendor/VendorRegister";
import VendorDashboard from "@/pages/vendor/VendorDashboard";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminVendorReview from "@/pages/admin/AdminVendorReview";
import AdminUsers from "@/pages/admin/AdminUsers";
import { AdminStaff, AdminRoles, AdminAudit } from "@/pages/admin/AdminAccess";
import { AdminHomeSections, AdminAttributes, AdminCatalogSettings } from "@/pages/admin/AdminCatalogConfig";
import { InventoryManager, ProductManager } from "@/components/catalog/ProductManager";
import AdminCategories from "@/pages/admin/AdminCategories";
import { AdminOrders, AdminSettings } from "@/pages/admin/AdminMisc";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/auth/PasswordPages";
import AccountLayout from "@/pages/account/AccountLayout";
import { ProfilePage, BusinessPage } from "@/pages/account/ProfilePages";
import AddressesPage from "@/pages/account/AddressesPage";
import { SecurityPage, NotificationsPage } from "@/pages/account/SecurityPages";
import VendorOnboarding from "@/pages/vendor/VendorOnboarding";

import { About, Contact, Legal } from "@/pages/static/StaticPages";
import { WalletPage, CurrencyExchange } from "@/pages/PlaceholderPages";
import Checkout from "@/pages/Checkout";
import { Orders, OrderDetail, OrderConfirmation } from "@/pages/Orders";
import { BusinessRfqs, BusinessRfqDetail } from "@/pages/business/RfqPages";
import { VendorRfqs, VendorRfqDetail } from "@/pages/vendor/VendorRfqs";
import AdminRfqs from "@/pages/admin/AdminRfqs";
import { PagePlaceholder } from "@/components/common/PagePlaceholder";

function NotFound() {
  return <PagePlaceholder title="Page Not Found" description="The page you are looking for doesn't exist." phase="marketplace" />;
}

function App() {
  return (
    <div className="App">
      <StoreProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/category/:slug" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/vendor/:slug" element={<VendorStore />} />
              <Route path="/store/:slug" element={<VendorStore />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/b2b" element={<B2B />} />
              <Route path="/b2b/products" element={<B2B />} />
              <Route path="/b2b/request-quote" element={<RequestQuote />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/referral" element={<Referral />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/vendor/register" element={<VendorRegister />} />

              <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
                <Route index element={<Account />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="business" element={<BusinessPage />} />
                <Route path="addresses" element={<AddressesPage />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
              </Route>
              <Route path="/profile" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}><Route index element={<ProfilePage />} /></Route>
              <Route path="/addresses" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}><Route index element={<AddressesPage />} /></Route>
              <Route path="/notifications" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}><Route index element={<NotificationsPage />} /></Route>
              <Route path="/vendor/onboarding" element={<ProtectedRoute roles={["vendor"]}><VendorOnboarding /></ProtectedRoute>} />
              <Route path="/vendor/products" element={<ProtectedRoute roles={["vendor"]}><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><ProductManager isAdmin={false} /></div></ProtectedRoute>} />
              <Route path="/vendor/inventory" element={<ProtectedRoute roles={["vendor"]}><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><InventoryManager /></div></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
              <Route path="/business/rfqs" element={<ProtectedRoute roles={["business_buyer"]}><BusinessRfqs /></ProtectedRoute>} />
              <Route path="/business/rfqs/:id" element={<ProtectedRoute roles={["business_buyer"]}><BusinessRfqDetail /></ProtectedRoute>} />
              <Route path="/vendor/rfqs" element={<ProtectedRoute roles={["vendor"]}><VendorRfqs /></ProtectedRoute>} />
              <Route path="/vendor/rfqs/:id" element={<ProtectedRoute roles={["vendor"]}><VendorRfqDetail /></ProtectedRoute>} />
              <Route path="/orders/confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
              <Route path="/orders/:orderNumber" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
              <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
              <Route path="/vendor/dashboard" element={<ProtectedRoute roles={["vendor", "admin", "staff"]}><VendorDashboard /></ProtectedRoute>} />

              <Route path="/currency-exchange" element={<CurrencyExchange />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Legal slug="terms" />} />
              <Route path="/privacy" element={<Legal slug="privacy" />} />
              <Route path="/refund-policy" element={<Legal slug="refund-policy" />} />
              <Route path="/shipping-policy" element={<Legal slug="shipping-policy" />} />
              <Route path="/vendor-policy" element={<Legal slug="vendor-policy" />} />

              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="vendors" element={<AdminVendorReview />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="roles" element={<AdminRoles />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="inventory" element={<InventoryManager />} />
                <Route path="homepage" element={<AdminHomeSections />} />
                <Route path="attributes" element={<AdminAttributes />} />
                <Route path="catalog-settings" element={<AdminCatalogSettings />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="rfqs" element={<AdminRfqs />} />
                <Route path="customers" element={<AdminUsers />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </StoreProvider>
    </div>
  );
}

export default App;
